import React from "react";

import { STATUS_COLOR } from "@/pages/admin/compliance/analytics/components/primitives";

/**
 * Where a candidate's documents currently sit, as one stacked bar.
 *
 * This replaced a row of six count chips on the candidate's Compliance tab. The
 * chips were accurate and told a recruiter nothing at a glance: reading "3, 0,
 * 2, 0, 5, 1" and working out that most of the pack is still with the candidate
 * is arithmetic, not a picture. One bar answers "how far along is this person"
 * in the time it takes to look at it.
 *
 * **Counts, not weight.** Every segment is a document count and the segments sum
 * to the total, so the bar and the number beside it can never disagree. This is
 * deliberately NOT the weighted percentage on the profile header pill — that one
 * decides whether the candidate clears the tenant's approval threshold, and it
 * is a different question from where the paperwork is. Two numbers, two
 * questions; the labels say which is which.
 *
 * Colour comes from `STATUS_COLOR`, the same map the analytics bars and the
 * dashboard donut are built from, so one status is one colour everywhere.
 */

// The order a document actually moves through, so the bar reads left to right
// as progress: nothing uploaded → with the candidate → waiting on an officer →
// sent back → settled. Taken from the chip row this replaced; `STATUS_COLOR`'s
// own key order is not this order, so never iterate that to build segments.
export const DOCUMENT_STAGES = [
  { key: "notUploaded", label: "Not uploaded" },
  { key: "pendingFromCandidate", label: "With candidate" },
  { key: "pendingForReview", label: "For review" },
  { key: "reRequested", label: "Re-requested" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
];

/**
 * @param {object} props
 * @param {object} props.progress  The tab's `progress` payload — one count per
 *   stage key. Missing keys read as 0.
 * @param {string} [props.className]
 */
const DocumentStageBar = ({ progress, className = "" }) => {
  const stages = DOCUMENT_STAGES.map((stage) => ({
    ...stage,
    count: Number(progress?.[stage.key]) || 0,
    color: STATUS_COLOR[stage.key],
  }));

  // The denominator is the sum of the segments, never `totalSlots`. The two can
  // differ — `notUploaded` is derived as `totalSlots - filled_slots`, so a slot
  // holding a superseded document counts once in a status and not at all in
  // `notUploaded` — and a bar whose parts do not fill their own track is a bar
  // that looks broken.
  const total = stages.reduce((sum, stage) => sum + stage.count, 0);

  return (
    <div className={className}>
      <div className="mb-2 flex items-baseline justify-between gap-3">
        <p className="text-[13px] font-semibold text-text-primary">Documents by stage</p>
        <p className="text-[12px] text-text-primary/60">
          {total} {total === 1 ? "document" : "documents"}
        </p>
      </div>

      {/* A candidate whose checklist resolved to nothing still gets a track
          rather than a gap in the card: an empty bar says "none yet", a missing
          one says the screen is broken. */}
      {total === 0 ? (
        <div className="h-2.5 w-full rounded bg-background-tertiary" />
      ) : (
        <div className="flex h-2.5 w-full overflow-hidden rounded" role="img" aria-label={stages.map((s) => `${s.label}: ${s.count}`).join(", ")}>
          {/* A zero-count stage renders no node at all. A 0%-width div still
              paints a hairline through `rounded`, which reads as a stage that
              has something in it. */}
          {stages.map((stage) =>
            stage.count > 0 ? (
              <div key={stage.key} style={{ width: `${(stage.count / total) * 100}%`, background: stage.color }} title={`${stage.label}: ${stage.count}`} />
            ) : null,
          )}
        </div>
      )}

      {/* Every stage keeps a legend row, including the empty ones: a zero in
          "Rejected" is exactly what a recruiter is looking for, and a stage that
          vanished when it hit zero would leave them unable to tell "none" from
          "not tracked". */}
      <div className="mt-2.5 flex flex-wrap gap-x-4 gap-y-1.5">
        {stages.map((stage) => (
          <span key={stage.key} className="flex items-center gap-1.5 text-[11.5px] text-text-secondary">
            <span className="h-2 w-2 shrink-0 rounded-sm" style={{ background: stage.color }} aria-hidden="true" />
            {stage.label}
            <span className="font-semibold text-text-primary">{stage.count}</span>
          </span>
        ))}
      </div>
    </div>
  );
};

export default DocumentStageBar;
